import {
  memo,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { BodyCopy } from "../components/BodyCopy";
import {
  Button,
  GradientText,
  Layout,
  SupportIcon,
} from "lola-framework-ui-test";
import { AuraLayout } from "../components/AuraLayout";
import { Popup } from "../components/Popup";
import { BarcodeDrawerSlot } from "../components/BarcodeDrawerSlot";
import { AmountMismatchTopDrawer } from "../components/AmountMismatchTopDrawer";
import { useStore } from "../store/globalState";
import { useSessionStore } from "../store/useSessionStore";
import { useCashDepositStore } from "../store/useCashDepositStore";
import { useRouteWithToken } from "../hooks/useRouteWithToken";
import { useCashDepositContext } from "../hooks/useCashDepositContext";
import { useCashStoresReelCenter } from "../hooks/useCashStoresReelCenter";
import { useSupportSheet } from "../hooks/useSupportSheet";
import { HowToDepositHelpIcon } from "../assets/icons";
import { ScrollFadeIndicator } from "../components/ScrollFadeIndicator";
import { APP_ROUTES } from "../constants";
import { accentInkOnSurface } from "../utils/accentInkOnSurface";
import { formatHeroAmount } from "../utils";
import {
  generateFundingBarcode,
  getFundingLocations,
  getWidgetApiError,
  isDeadLinkError,
} from "../services/api";
import type { IFundingMerchant } from "../interfaces/cash-deposit";
import { getEvents, safeCapture } from "../analytics";
import {
  isFundingPolicyReady,
  refreshesBarcodeOnAmountMismatch,
  requiresCustomerToChooseChain,
} from "../utils/funding-policy";
import { buildAlreadyFundedErrorState } from "../utils/already-funded";
import { useLiveExitUrl } from "../hooks/useLiveExitUrl";
import { unlockAmountMismatchAudio } from "../utils/playAmountMismatchAlert";
import { Navbar } from "../components/Navbar";

/** How many times the merchant directory is repeated to build the spinning reel.
 *  Three copies is enough to spin across one full directory and still show
 *  neighbors on both sides of the settled slot. */
const REEL_LOOPS = 3;

const MerchantLogo = ({ merchant }: { merchant: IFundingMerchant }) => {
  const [failed, setFailed] = useState(false);

  if (failed || !merchant.logoUrl) {
    return (
      <span className="cash-stores-logo-fallback" aria-hidden>
        {merchant.name}
      </span>
    );
  }

  return (
    <img
      src={merchant.logoUrl}
      alt={merchant.name}
      className="cash-stores-logo"
      onError={() => setFailed(true)}
    />
  );
};

/**
 * Cash-stores summary screen. Shows the cash amount and on the primary CTA
 * mints a barcode. When InComm policy asks the customer to pick a chain, shows
 * the selected-merchant reel; otherwise skips StoresPage and POSTs without
 * merchantId so the backend resolves the wildcard.
 */
export const CashStoresPage = memo(() => {
  const { t } = useTranslation();
  const { theme, setErrorState } = useStore();
  const { session } = useSessionStore();
  const {
    merchants,
    selectedMerchantId,
    instrument,
    setMerchants,
    setInstrument,
    pendingMismatch,
    setPendingMismatch,
    refreshingBarcode,
    setRefreshingBarcode,
    refreshedMismatchKey,
    setRefreshedMismatchKey,
  } = useCashDepositStore();
  const [showBarcodeDrawer, setShowBarcodeDrawer] = useState(false);
  const [generating, setGenerating] = useState(false);
  const colorConfig = theme.whiteView;
  const {
    background,
    backgroundBtn,
    textColorBtn,
    bodyCopy,
    footerColor,
    title,
    dropzoneColors,
  } = colorConfig;
  const partnerColors = session.data?.partner?.theme?.colors as
    | Record<string, string>
    | undefined;
  const howToTitle = accentInkOnSurface(partnerColors, background, bodyCopy);
  const { redirectTo } = useRouteWithToken();
  const { context } = useCashDepositContext();
  const callbackUrl = useLiveExitUrl();

  const policyReady = isFundingPolicyReady(context);
  const askForChain = requiresCustomerToChooseChain(context);

  useEffect(() => {
    if (!policyReady) return;
    safeCapture(getEvents().SCREEN_VIEW("cash_stores"), {
      operation_id: context?.operationId,
      require_customer_to_choose_chain: askForChain,
    });
    // Fire once the InComm policy is known; operation_id is best-effort.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [policyReady]);

  // When Superadmin requires a chain pick, a refresh without selection goes
  // back to the picker. Otherwise mint omits merchantId (backend wildcard).
  useEffect(() => {
    if (!policyReady) return;
    if (askForChain && !selectedMerchantId) {
      redirectTo(APP_ROUTES.STORES_PAGE, { replace: true });
    }
  }, [policyReady, askForChain, selectedMerchantId, redirectTo]);

  // Reel needs the chain directory. StoresPage usually fills it; refetch if
  // the user lands here with a selection but an empty store (e.g. soft nav).
  useEffect(() => {
    if (!askForChain || merchants.length > 0) return;
    let cancelled = false;
    (async () => {
      try {
        const data = await getFundingLocations();
        if (!cancelled) setMerchants(data);
      } catch {
        // Non-fatal: reel stays empty until a retry path loads merchants.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [askForChain, merchants.length, setMerchants]);

  useEffect(() => {
    if (pendingMismatch && instrument) {
      setShowBarcodeDrawer(true);
    }
  }, [pendingMismatch, instrument]);

  // The deposit amount/currency are pinned by the operation for
  // operation-funded links (resolved via the context endpoint); standalone
  // links carry the amount inline on the link config.
  const isStandalone = session.data?.config?.type === "standalone";
  const operationId = context?.operationId;

  // InComm may burn the barcode after a wrong-amount attempt. When Superadmin
  // enables it, mint a replacement in the background: the drawer and the
  // mismatch warning stay up while the strip shows a spinner. One refresh per
  // attempt; on failure the current barcode stays.
  const refreshOnMismatch = refreshesBarcodeOnAmountMismatch(context);
  const mismatchKey = pendingMismatch
    ? pendingMismatch.attemptRef ?? pendingMismatch.occurredAt
    : null;

  useEffect(() => {
    if (!refreshOnMismatch || !mismatchKey || !operationId) return;
    if (refreshedMismatchKey === mismatchKey) return;
    const current = useCashDepositStore.getState().instrument;
    if (!current) return;

    setRefreshedMismatchKey(mismatchKey);
    setRefreshingBarcode(true);
    (async () => {
      try {
        const fresh = await generateFundingBarcode(operationId, current.merchantId, {
          refreshReason: "amount_mismatch",
        });
        if (useCashDepositStore.getState().instrument?.ref !== current.ref) return;
        setInstrument(fresh);
        safeCapture(getEvents().BARCODE_CREATED, {
          success: true,
          operation_id: operationId,
          merchant_id: fresh.merchantId,
          refresh_reason: "amount_mismatch",
        });
      } catch (err) {
        console.warn("Barcode refresh after amount mismatch failed", err);
        safeCapture(getEvents().BARCODE_CREATED, {
          success: false,
          operation_id: operationId,
          merchant_id: current.merchantId,
          refresh_reason: "amount_mismatch",
          error_code: getWidgetApiError(err)?.code ?? "BARCODE_REFRESH_FAILED",
        });
      } finally {
        setRefreshingBarcode(false);
      }
    })();
  }, [
    refreshOnMismatch,
    mismatchKey,
    operationId,
    refreshedMismatchKey,
    setRefreshedMismatchKey,
    setRefreshingBarcode,
    setInstrument,
  ]);

  const amount = isStandalone
    ? session.data?.config?.amount
    : context?.amount?.value ?? instrument?.amount;
  const currency = isStandalone
    ? session.data?.config?.sourceCurrency
    : context?.amount?.currency ?? instrument?.currency;
  const resolvedCurrency = currency ?? "USD";
  const heroAmount =
    amount != null ? formatHeroAmount(amount, resolvedCurrency) : null;

  // Slot-machine reel only when Superadmin requires an explicit chain pick.
  const reel = useMemo(() => {
    if (!askForChain || merchants.length === 0) return [];
    const arr: IFundingMerchant[] = [];
    for (let i = 0; i < REEL_LOOPS; i++) arr.push(...merchants);
    return arr;
  }, [askForChain, merchants]);

  const selectedIndex = askForChain
    ? merchants.findIndex((m) => m.merchantId === selectedMerchantId)
    : -1;
  const targetIndex =
    selectedIndex < 0
      ? -1
      : (REEL_LOOPS - 2) * merchants.length + selectedIndex;

  const {
    viewportRef,
    trackRef,
    targetRef,
    translate,
    reelPhase,
    handleReelEnd,
  } = useCashStoresReelCenter(targetIndex, reel.length);

  // The barcode can only be minted against an operation parked in
  // awaiting_funding (operation_payment links). Standalone links have no
  // operation yet, so the CTA stays disabled.
  const canCreateBarcode = Boolean(
    operationId && !generating && (askForChain ? selectedMerchantId : true),
  );

  const handleCreateBarcode = async () => {
    if (!operationId) return;
    if (askForChain && !selectedMerchantId) return;
    setGenerating(true);
    try {
      const issued = await generateFundingBarcode(
        operationId,
        askForChain ? selectedMerchantId ?? undefined : undefined,
      );
      setInstrument(issued);
      unlockAmountMismatchAudio();
      setShowBarcodeDrawer(true);
      safeCapture(getEvents().BARCODE_CREATED, {
        success: true,
        operation_id: operationId,
        merchant_id: askForChain ? selectedMerchantId : issued.merchantId,
      });
    } catch (err) {
      const apiError = getWidgetApiError(err);
      safeCapture(getEvents().BARCODE_CREATED, {
        success: false,
        operation_id: operationId,
        merchant_id: selectedMerchantId,
        error_code: apiError?.code ?? "BARCODE_GENERATION_FAILED",
      });

      // Link expired / revoked: the dead-link handler already routed to the
      // screen that explains why the link stopped working.
      if (isDeadLinkError(err)) {
        return;
      }

      if (apiError?.code === "USER_BLOCKED") {
        redirectTo(APP_ROUTES.ERROR_PAGE, {
          replace: true,
          state: {
            title: t("error.depositUnavailableTitle"),
            subTitle: null,
            message: t("error.depositUnavailableMessage"),
            btnText: t("common.needHelp"),
            isOut: false,
            pageUrl: APP_ROUTES.SUPPORT_PAGE,
          },
        });
        return;
      }

      if (apiError?.code === "OPERATION_NOT_AWAITING_FUNDING") {
        setErrorState(buildAlreadyFundedErrorState(t, callbackUrl));
      } else {
        setErrorState({
          title: t("error.title"),
          subTitle: t("error.barcodeGenerationFailed"),
          message: apiError?.message || t("error.tryAgainLater"),
          btnText: t("common.retry"),
          isOut: false,
          pageUrl: APP_ROUTES.CASH_STORES_PAGE,
        });
      }

      redirectTo(APP_ROUTES.ERROR_PAGE);
    } finally {
      setGenerating(false);
    }
  };

  const handleBack = () => {
    setShowBarcodeDrawer(false);
    setInstrument(null);
    redirectTo(
      askForChain
        ? APP_ROUTES.STORES_PAGE
        : APP_ROUTES.CASH_DEPOSIT_LANDING_PAGE,
    );
  };

  const themeVars = {
    "--cash-stores-accent": backgroundBtn,
    "--cash-stores-amount-border": bodyCopy,
    "--cash-stores-card-border": bodyCopy,
    "--cash-stores-card-surface": background,
    "--how-to-title": howToTitle,
  } as React.CSSProperties;

  const { openSupport, supportSheet } = useSupportSheet();

  return (
    <>
      <AuraLayout
        colorConfig={colorConfig}
        className="cash-stores-page need-help-header"
      >
        <Layout.Header style={themeVars}>
          <Navbar
            title={t("transactions.needHelp")}
            align="right"
            color={bodyCopy}
            onBackClick={handleBack}
            onIconClick={() => openSupport()}
            icon={<SupportIcon size={35} colors={dropzoneColors} />}
          />
        </Layout.Header>

        <Layout.Content className="cash-stores-section" style={themeVars}>
          <ScrollFadeIndicator color={bodyCopy} />
          <section className="cash-stores-copy">
            <GradientText
              as="h1"
              textColor={bodyCopy}
              className="cash-stores-title"
            >
              {t("cashStores.title")}
            </GradientText>
            <BodyCopy textColor={bodyCopy} className="cash-stores-subtitle">
              {t("cashStores.subtitle")}
            </BodyCopy>
          </section>

          {askForChain && reel.length > 0 ? (
            <section
              className="cash-stores-reel"
              data-phase={reelPhase}
              aria-label={t("stores.merchantsLabel")}
            >
              <div className="cash-stores-reel-viewport" ref={viewportRef}>
                <div
                  className="cash-stores-reel-track"
                  ref={trackRef}
                  style={{ transform: `translateX(${translate}px)` }}
                  onTransitionEnd={handleReelEnd}
                >
                  {reel.map((merchant, index) => {
                    const isTarget = index === targetIndex;
                    return (
                      <div
                        key={`${merchant.merchantId}-${index}`}
                        ref={isTarget ? targetRef : undefined}
                        className={`cash-stores-reel-item${
                          isTarget && reelPhase === "landed" ? " is-selected" : ""
                        }`}
                        aria-hidden={!isTarget}
                      >
                        <MerchantLogo merchant={merchant} />
                      </div>
                    );
                  })}
                </div>
                <span className="cash-stores-reel-fade cash-stores-reel-fade--left" />
                <span className="cash-stores-reel-fade cash-stores-reel-fade--right" />
              </div>
            </section>
          ) : null}

          <section
            className="cash-stores-amount-card"
            aria-label={t("cashStores.amountLabel")}
          >
            <BodyCopy textColor={bodyCopy} className="cash-stores-amount-label">
              {t("cashStores.amountLabel")}
            </BodyCopy>
            <GradientText
              as="p"
              textColor={bodyCopy}
              className="cash-stores-amount-value"
            >
              {heroAmount ? (
                <>
                  {heroAmount.prefix}
                  {heroAmount.intPart}.<label>{heroAmount.decPart}</label>
                  <span className="cash-stores-amount-currency">
                    {heroAmount.suffix}
                  </span>
                </>
              ) : (
                "—"
              )}
            </GradientText>
          </section>

          <button
            type="button"
            className="cash-stores-how-to"
            onClick={() => redirectTo(APP_ROUTES.HOW_TO_PAY_IN_CASH_PAGE)}
          >
            <span className="cash-stores-how-to-mark">
              <span className="cash-stores-how-to-mark-shadow" aria-hidden />
              <span
                className="cash-stores-how-to-mark-disc"
                style={{ background }}
              >
                <HowToDepositHelpIcon
                  size={18}
                  colors={dropzoneColors}
                />
              </span>
            </span>
            <GradientText
              as="span"
              textColor={bodyCopy}
              className="cash-stores-how-to-label"
            >
              {t("cashStores.howToDeposit")}
            </GradientText>
          </button>
        </Layout.Content>

        <Layout.Footer style={themeVars}>
          <section className="valid-cards">
            <section className="valid-cards-container cash-stores-disclaimer-block">
              <BodyCopy
                className="cash-stores-disclaimer-line"
                textColor={footerColor}
              >
                {t("cashStores.disclaimerLine1")}{" "}
                {t("cashStores.disclaimerLine2")}{" "}
                <GradientText
                  as="span"
                  className="cash-stores-terms-link"
                  textColor={title}
                  onClick={() => redirectTo(APP_ROUTES.LEGAL_PAGE)}
                >
                  {t("cashStores.termsLink")}
                </GradientText>
                .
              </BodyCopy>
            </section>
            <section className="footer-section">
              <Button
                size="large"
                background={backgroundBtn}
                color={textColorBtn}
                disabled={!canCreateBarcode}
                onClick={handleCreateBarcode}
              >
                {t("cashStores.cta")}
              </Button>
            </section>
          </section>
        </Layout.Footer>
      </AuraLayout>
      <Popup
        visible={showBarcodeDrawer}
        onClose={() => setShowBarcodeDrawer(false)}
      >
        <>
          {pendingMismatch ? (
            <AmountMismatchTopDrawer
              attempt={pendingMismatch}
              onDismiss={() => setPendingMismatch(null)}
            />
          ) : null}
          {instrument && (
            <BarcodeDrawerSlot
              amount={instrument.amount}
              currency={instrument.currency}
              barcodeValue={instrument.data}
              expMinutes={instrument.expMinutes}
              expiresAt={instrument.expiresAt}
              refreshing={refreshingBarcode}
              onClose={() => setShowBarcodeDrawer(false)}
            />
          )}
        </>
      </Popup>
      {supportSheet}
    </>
  );
});
